import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Phone, Mail, MapPin, MessageSquare, Clock, Award } from "lucide-react";
import { FaFacebook, FaInstagram } from 'react-icons/fa';
import { MapImage } from "./MapImage";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-neutral-800 text-white bg-[linear-gradient(180deg,#232323_70%,rgba(35,35,35,0.7)_100%)]">
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          
          {/* Company Info */}
          <div className="space-y-4">
            <div className="font-playfair text-3xl font-bold text-wood-warm mb-4">
              El Samán
            </div>
            <p className="text-white/80 leading-relaxed">
              Especialistas en acabados residenciales de lujo. Transformamos espacios con productos premium 
              y asesoría personalizada.
            </p>
            <div className="flex items-center space-x-2 text-sm">
              <Award size={16} className="text-wood-warm" />
              <span className="text-white/80">15+ años de experiencia</span>
            </div>
            
            {/* Redes Sociales */}
            <div className="mt-4">
              <h4 className="text-wood-warm font-medium mb-2">Síguenos en:</h4>
              <div className="flex space-x-4">
                <a 
                  href="https://www.facebook.com/profile.php?id=61581649137489&mibextid=wwXIfr" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-white hover:text-wood-warm transition-colors"
                  aria-label="Facebook de El Samán"
                >
                  <FaFacebook size={24} />
                </a>
                <a 
                  href="https://www.instagram.com/el_saman_interiores?igsh=MWRzdnc5MDNrdWdibg==" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-white hover:text-wood-warm transition-colors"
                  aria-label="Instagram de El Samán"
                >
                  <FaInstagram size={24} />
                </a>
              </div>
            </div>
            <div className="mt-6">
              <h3 className="font-playfair text-xl font-semibold text-wood-warm mb-4">Síguenos en Redes</h3>
              <div className="space-y-3">
                <a 
                  href="https://www.facebook.com/profile.php?id=61581649137489&mibextid=wwXIfr" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center space-x-2 text-white/80 hover:text-wood-warm transition-colors group"
                >
                  <FaFacebook size={20} className="text-wood-warm" />
                  <span>Facebook</span>
                </a>
                <a 
                  href="https://www.instagram.com/el_saman_interiores?igsh=MWRzdnc5MDNrdWdibg==" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center space-x-2 text-white/80 hover:text-wood-warm transition-colors group"
                >
                  <FaInstagram size={20} className="text-wood-warm" />
                  <span>Instagram</span>
                </a>
              </div>
            </div>
          </div>

          {/* Navigation Links */}
          <div className="space-y-4">
            <h3 className="font-playfair text-xl font-semibold text-wood-warm">
              Navegación
            </h3>
            <ul className="space-y-2">
              <li>
                <button 
                  onClick={() => scrollToSection('inicio')}
                  className="text-white/80 hover:text-wood-warm transition-colors text-left"
                >
                  Inicio
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('productos')}
                  className="text-white/80 hover:text-wood-warm transition-colors text-left"
                >
                  Productos
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('galeria')}
                  className="text-white/80 hover:text-wood-warm transition-colors text-left"
                >
                  Galería
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('nosotros')}
                  className="text-white/80 hover:text-wood-warm transition-colors text-left"
                >
                  Nosotros
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('contacto')}
                  className="text-white/80 hover:text-wood-warm transition-colors text-left"
                >
                  Contacto
                </button>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div className="space-y-4">
            <h3 className="font-playfair text-xl font-semibold text-wood-warm">
              Lo que ofrecemos
            </h3>
            <ul className="space-y-2 text-white/80">
              <li>• Lambrines Extruidos</li>
              <li>• Lambrines Coextruidos</li>
              <li>• Pisos Vinílicos</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h3 className="font-playfair text-xl font-semibold text-wood-warm">
              Contacto
            </h3>
            <div className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Phone size={16} className="text-wood-warm flex-shrink-0" />
                  <a href="tel:2212512363" className="text-white/80 hover:text-wood-warm transition-colors">
                    221 251 2363
                  </a>
                </div>
                <div className="flex items-center space-x-3">
                  <MessageSquare size={16} className="text-wood-warm flex-shrink-0" />
                  <a href="https://wa.me/5212212512363" className="text-white/80 hover:text-wood-warm transition-colors">
                    221 251 2363
                  </a>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail size={16} className="text-wood-warm flex-shrink-0" />
                  <a href="mailto:clientes@elsaman.com.mx" className="text-white/80 hover:text-wood-warm transition-colors">
                    clientes@elsaman.com.mx
                  </a>
                </div>
                <div className="flex items-start space-x-3">
                  <MapPin size={16} className="text-wood-warm flex-shrink-0 mt-0.5" />
                  <span className="text-white/80">
                    Calle 13 Nte 1104, El Tamborcito, 72000 Heroica Puebla de Zaragoza, Pue.
                  </span>
                </div>
              </div>
              
              <div className="pt-3">
                <MapImage 
                  address="Calle 13 Nte 1104, El Tamborcito, 72000 Heroica Puebla de Zaragoza, Pue."
                  staticImageUrl="/images/mapa-saman.png"
                  googleLink="https://www.google.com/maps/search/?api=1&query=Calle+13+Nte+1104,+El+Tamborcito,+72000+Heroica+Puebla+de+Zaragoza,+Pue."
                  height={180}
                  className="rounded-lg overflow-hidden border border-gray-200/20"
                />
              </div>
            </div>

            {/* Business Hours */}
            <div className="pt-4 border-t border-ivory/20">
              <div className="flex items-center space-x-2 mb-2">
                <Clock size={16} className="text-wood-warm" />
                <span className="text-wood-warm font-medium">Horarios</span>
              </div>
              <div className="text-sm text-white/80 space-y-1">
                <div>Lun - Vie: 9:00 AM - 6:00 PM</div>
                <div>Sábados: 9:00 AM - 3:00 PM</div>
                <div>Domingos: Cerrado</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Separator className="bg-ivory/20" />

      {/* Bottom Bar */}
      <div className="container mx-auto px-4 py-6">
        <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <div className="text-sm text-white/60">
            {currentYear} El Samán. Todos los derechos reservados.
          </div>
          <div className="flex space-x-6 text-sm">
            <a href="#" className="text-white/60 hover:text-wood-warm transition-colors">
              Política de Privacidad
            </a>
            <a href="#" className="text-white/60 hover:text-wood-warm transition-colors">
              Términos de Servicio
            </a>
            <a href="#" className="text-white/60 hover:text-wood-warm transition-colors">
              Garantías
            </a>
          </div>
        </div>
      </div>

      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/5212212512363"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 bg-green-500 text-white p-4 rounded-full shadow-lg hover:bg-green-600 transition-colors z-50 group" aria-label="Chatear por WhatsApp" title="Chatear por WhatsApp"
      >
        <MessageSquare size={24} className="group-hover:scale-110 transition-transform" />
      </a>
    </footer>
  );
};

export default Footer;